/**
 * Locale detection utility.
 *
 * Priority order:
 *  1. URL path prefix  (e.g. /de/lobby  →  'de')
 *  2. Player profile language preference (passed in from user store after auth)
 *  3. localStorage key 'locale'
 *  4. navigator.language  (e.g. 'en-US'  →  'en')
 *  5. Fallback: 'en'
 */

const SUPPORTED = ['en', 'de', 'pt', 'es', 'fr', 'tr'] as const
type SupportedLocale = (typeof SUPPORTED)[number]

/**
 * Normalises an arbitrary locale string to a supported 2-letter code,
 * or returns null if the code is not in the supported list.
 */
function normalize(code: string | null | undefined): SupportedLocale | null {
    if (!code) return null
    const lc = code.slice(0, 2).toLowerCase()
    return (SUPPORTED as readonly string[]).includes(lc) ? (lc as SupportedLocale) : null
}

/**
 * Extracts the locale from the first URL path segment.
 * E.g. /de/lobby → 'de',  /en-US/page → 'en',  /lobby → null
 */
function fromUrlPrefix(): SupportedLocale | null {
    // pathname always starts with '/', so split('/')[0] is empty, [1] is the first segment
    const segment = window.location.pathname.split('/')[1]
    return normalize(segment)
}

/**
 * Returns the best-matching supported locale for the current session.
 *
 * @param userLanguage  Optional language field from the authenticated user's
 *                      profile (resources/js/stores/user.ts → user.language).
 *                      Pass this after login to incorporate the player's saved
 *                      preference.
 */
export function detectLocale(userLanguage?: string | null): SupportedLocale {
    return (
        fromUrlPrefix() ??
        normalize(userLanguage) ??
        normalize(localStorage.getItem('locale')) ??
        normalize(navigator.language) ??
        'en'
    )
}
